import React, { Component } from 'react';
import DatePicker from 'react-datepicker';

import { getSelectedBranchId } from '../utils/branch';

var config = process.env;

// Resumen del día (ENI-268).
//
// Una sola pantalla que responde "¿cómo estuvo tal día?". No es el Pulso con un selector: el
// Pulso mezcla el período con el estado de AHORA (alertas, comandas pendientes, mesas ocupadas en
// este momento), y eso bajo una fecha pasada sería directamente falso. Acá todo está acotado al
// día elegido.
//
// Reglas de presentación, iguales al resto de la épica:
//   - lo que no se puede calcular se muestra "—", NUNCA 0;
//   - lo parcial se declara parcial.

const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString('de-DE', { maximumFractionDigits: 0 })}`);
const number = (v) => (v == null ? '—' : Number(v).toLocaleString('de-DE'));

// Por qué "Queda después de todo" puede no ser el resultado sino un techo. Son DOS motivos
// distintos y hay que decir cuál: la tasa del POS sin cargar (se arregla tipeando un número) y la
// comisión de Mercado Pago sin medir (no se arregla: donde no hay cobro vinculado no hay dato, y
// estimarla con un promedio sería inventar). Colapsarlos en un solo cartel manda a revisar la
// pantalla equivocada.
// Qué se le restó (y sumó) a lo vendido para llegar al ingreso del día. `null` si no hubo nada.
// Las gift cards van igual que los usados (decisión del dueño, 2026-10-08): la vendida es plata
// cobrada que todavía se debe en mercadería; la que venció sin usarse ya no se debe y vuelve a
// sumar. Mismo criterio que en SalesAnalyticsComponent.
function pasoAlIngreso(summary) {
    const usados = summary.usedGames && summary.usedGames.credit > 0 ? summary.usedGames : null;
    const gc = summary.giftCards || {};
    const partes = [];
    if (usados) partes.push(`menos ${money(usados.credit)} de crédito de ${number(usados.units)} usado(s) vendido(s), que es de los vendedores`);
    if (gc.sold > 0) partes.push(`menos ${money(gc.sold)} en ${number(gc.units)} gift card(s) vendida(s), que todavía se deben en mercadería`);
    if (gc.expired > 0) partes.push(`más ${money(gc.expired)} de gift cards que vencieron sin usarse`);
    if (gc.given > 0) partes.push(`menos ${money(gc.given)} de premios y cortesías entregados (regalado)`);
    return partes.length > 0 ? partes.join(', ') : null;
}

function ayudaDelMargen(cob) {
    if (!cob) { return 'con IVA, comisiones y envío descontados'; }
    if (!cob.cardRatesConfigured) { return 'es un TECHO: la comisión de tarjeta no está cargada'; }
    if (cob.mpSales > 0 && cob.mpSalesWithPayment < cob.mpSales) {
        return `es un TECHO: la comisión de Mercado Pago está medida en ${number(cob.mpSalesWithPayment)}`
            + ` de ${number(cob.mpSales)} ventas; en el resto no se descuenta`;
    }
    return 'con IVA, comisiones y envío descontados';
}

function toIsoDate(date) {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

/** Una variación con su color y su signo. `null` cuando la base es 0: dividir por cero no es 0%. */
function Variacion({ pct }) {
    if (pct == null) return <span className="text-muted">—</span>;
    const positivo = pct >= 0;
    return (
        <span className={positivo ? 'text-success' : 'text-danger'}>
            {positivo ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}%
        </span>
    );
}

class DayAnalyticsComponent extends Component {
    constructor(props) {
        super(props);
        // El día puede venir en la URL (?date=2026-08-09): así el resumen de un día concreto se
        // puede compartir por mensaje, que es como se usa en la práctica.
        //
        // Sin parámetro arranca en AYER y no en hoy: la pregunta que motiva la pantalla es "cómo
        // estuvo", en pasado, y un día a medio andar se lee peor.
        this.state = { dia: this.diaInicial(), canalTop: null, loaded: false, error: null, data: null };
    }

    /** Lee ?date=YYYY-MM-DD de la URL. Por componentes: `new Date('2026-08-09')` se parsea como
     *  UTC y en Uruguay cae el día anterior. */
    diaInicial() {
        const cruda = new URLSearchParams(window.location.search).get('date');
        if (cruda && /^\d{4}-\d{2}-\d{2}$/.test(cruda)) {
            const [y, m, d] = cruda.split('-').map(Number);
            const parsed = new Date(y, m - 1, d);
            // `new Date(2026, 12, 45)` NO da NaN: hace rollover a 2027-02-14. Un typo en un link
            // compartido mostraba otro día sin avisar, así que se comprueba que los componentes
            // sobrevivan al viaje de ida y vuelta.
            const redondo = parsed.getFullYear() === y && parsed.getMonth() === m - 1 && parsed.getDate() === d;
            if (redondo) return parsed;
        }
        const ayer = new Date();
        ayer.setDate(ayer.getDate() - 1);
        return ayer;
    }

    componentDidMount() { this.cargar(); }

    cargar = () => {
        this.setState({ loaded: false, error: null });
        const branchId = getSelectedBranchId();
        const pedido = toIsoDate(this.state.dia);
        const params = new URLSearchParams({ date: pedido });
        if (branchId) params.set('branchId', branchId);

        // Token de secuencia: gana el ÚLTIMO PEDIDO, no la última respuesta. Los botones ◀ ▶
        // encolan un pedido por clic, y si el día más pesado tarda más, su respuesta llegaba
        // después y pisaba a la del día elegido: la pantalla quedaba mostrando otro día bajo la
        // fecha del picker. En una pantalla cuyo link se comparte eso es lo peor que puede pasar
        // — el link dice un día y los números son de otro.
        this._pedido = pedido;

        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}analytics/day?${params.toString()}`, {
            method: 'GET',
            headers: { 'access-token': localStorage.getItem('token'), 'Content-Type': 'application/json' },
        })
            .then((r) => r.json())
            .then((body) => {
                // Llegó tarde: hay un pedido más nuevo en vuelo. Se descarta en silencio.
                if (this._pedido !== pedido || this._desmontado) return;
                if (body.error > 0) {
                    if (body.error === 1002) { window.location.href = localStorage.getItem('homePageURL'); return; }
                    this.setState({ loaded: true, error: body.message, data: null });
                    return;
                }
                // La URL se escribe cuando LLEGAN los datos, no al disparar: si se escribiera al
                // disparar, un pedido que después se descarta dejaría la URL apuntando a un día
                // que la pantalla nunca llegó a mostrar.
                window.history.replaceState(null, '', `${window.location.pathname}?date=${pedido}`);
                this.setState({ loaded: true, data: body.data, error: null, canalTop: null });
            })
            .catch((e) => {
                if (this._pedido !== pedido || this._desmontado) return;
                this.setState({ loaded: true, error: e.message, data: null });
            });
    };

    componentWillUnmount() { this._desmontado = true; }

    mover = (dias) => {
        // Guarda contra una fecha inválida: si el campo quedó vacío, `this.state.dia` es null y
        // `new Date(null)` es el epoch — clickear ◀ desde ahí llevaba a 1969-12-30.
        const base = this.state.dia instanceof Date && !Number.isNaN(this.state.dia.getTime())
            ? this.state.dia
            : new Date();
        const d = new Date(base);
        d.setDate(d.getDate() + dias);
        this.setState({ dia: d }, this.cargar);
    };

    /**
     * El día mesa por mesa, ordenado por personas.
     *
     * Se listan TODAS las mesas activas, incluidas las que quedaron en cero: una mesa que nadie
     * eligió es justamente el dato que se busca.
     */
    renderMesas(mesas) {
        if (!mesas || mesas.length === 0) {
            return <div className="p-3"><p className="text-muted mb-0">No hay mesas activas.</p></div>;
        }
        const sinUsar = mesas.filter((m) => m.stays === 0).length;
        return (
            <div>
                <table className="table table-sm mb-0">
                    <thead className="bg-light">
                        <tr>
                            <th className="border-0">Mesa</th>
                            <th className="border-0 text-right">Personas</th>
                            <th className="border-0 text-right">Estadías</th>
                            <th className="border-0 text-right">Aprovechada</th>
                            <th className="border-0 text-right">Ingreso</th>
                            <th className="border-0 text-right">Por persona</th>
                        </tr>
                    </thead>
                    <tbody>
                        {mesas.map((m) => (
                            <tr key={m.tableId} className={m.stays === 0 ? 'text-muted' : ''}>
                                <td>
                                    {m.label || `#${m.tableId}`}
                                    <span className="text-muted" style={{ fontSize: 11 }}> · {m.capacity} lugares</span>
                                    {m.isVip && <span className="badge badge-light ml-1">VIP</span>}
                                </td>
                                <td className="text-right">{m.stays === 0 ? '—' : number(m.people)}</td>
                                <td className="text-right">{m.stays === 0 ? '—' : number(m.stays)}</td>
                                {/* Cuánto de la mesa se usó: 2 personas en una mesa de 6 son 33%.
                                    Es la diferencia entre "la mesa estuvo ocupada" y "la mesa
                                    estuvo aprovechada", que no es lo mismo. */}
                                <td className="text-right">{m.fillPct == null ? '—' : `${m.fillPct.toFixed(0)}%`}</td>
                                <td className="text-right">{m.stays === 0 ? '—' : money(m.revenue)}</td>
                                <td className="text-right">{money(m.spendPerPerson)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="px-3 py-2 border-top text-muted" style={{ fontSize: 12 }}>
                    {sinUsar > 0
                        ? <>{number(sinUsar)} de {number(mesas.length)} mesas no se usaron ese día.</>
                        : <>Se usaron las {number(mesas.length)} mesas.</>}
                    {' '}El ingreso es lo que se cobró EN la mesa: la entrada y lo que se le cargó.
                    El consumo que no quedó asociado a ninguna reserva no se le atribuye a ninguna mesa.
                </div>
            </div>
        );
    }

    renderKpi(rotulo, valor, ayuda) {
        return (
            <div className="col-lg col-md-4 col-sm-6 mb-3">
                <div className="card card-small h-100"><div className="card-body p-3">
                    <span className="text-uppercase text-muted d-block" style={{ fontSize: 11 }}>{rotulo}</span>
                    <h6 className="my-2">{valor}</h6>
                    {ayuda && <span className="d-block text-muted" style={{ fontSize: 11 }}>{ayuda}</span>}
                </div></div>
            </div>
        );
    }

    /** Barras horizontales simples: para 3-6 filas se leen mejor que un gráfico. */
    renderBarras(titulo, filas, ayuda) {
        const max = filas.reduce((m, f) => Math.max(m, f.revenue || 0), 0);
        return (
            <div className="col-lg-4 col-md-6 mb-4">
                <div className="card card-small h-100">
                    <div className="card-header border-bottom">
                        <h6 className="m-0">{titulo}</h6>
                        {ayuda && <span className="text-muted" style={{ fontSize: 12 }}>{ayuda}</span>}
                    </div>
                    <div className="card-body p-3">
                        {filas.length === 0 && <p className="text-muted mb-0">Sin ventas ese día.</p>}
                        {filas.map((f) => (
                            <div key={f.key || f.name || f.paymentType} className="mb-2">
                                <div className="d-flex justify-content-between" style={{ fontSize: 13 }}>
                                    <span>{f.key || f.name}</span>
                                    <span className="text-muted">{money(f.revenue)}</span>
                                </div>
                                <div style={{ background: '#eef0f3', height: 6, borderRadius: 3 }}>
                                    <div style={{
                                        width: max > 0 ? `${Math.max(2, (f.revenue / max) * 100)}%` : '0%',
                                        background: '#007bff', height: 6, borderRadius: 3,
                                    }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    /** La curva del día por hora, en barras. El pico se marca aparte. */
    renderHoras(hourly) {
        const horas = hourly.hours || [];
        const max = horas.reduce((m, h) => Math.max(m, h.revenue), 0);
        // Se dibujan TODAS las horas con ventas, no un rango fijo: el local no abre siempre a la
        // misma hora y rellenar de 0 a 23 con ceros haría ver un día vacío.
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom">
                    <h6 className="m-0">Cuándo se vendió</h6>
                    <span className="text-muted" style={{ fontSize: 12 }}>
                        {hourly.peak
                            ? <>La hora más fuerte fue las <strong>{hourly.peak.hour}:00</strong> con {money(hourly.peak.revenue)} en {number(hourly.peak.orders)} ventas. El pico se elige por plata, no por cantidad de tickets.</>
                            : 'Sin ventas ese día.'}
                    </span>
                </div>
                <div className="card-body p-3">
                    <div className="d-flex align-items-end" style={{ height: 140, gap: 4 }}>
                        {horas.map((h) => (
                            <div key={h.hour} className="text-center" style={{ flex: 1, minWidth: 18 }} title={`${h.hour}:00 · ${money(h.revenue)} · ${h.orders} ventas`}>
                                <div style={{
                                    height: max > 0 ? `${Math.max(3, (h.revenue / max) * 110)}px` : '3px',
                                    background: hourly.peak && h.hour === hourly.peak.hour ? '#007bff' : '#a8c7f0',
                                    borderRadius: '3px 3px 0 0',
                                }} />
                                <span className="text-muted" style={{ fontSize: 10 }}>{h.hour}</span>
                            </div>
                        ))}
                        {horas.length === 0 && <span className="text-muted">Sin ventas ese día.</span>}
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { loaded, error, data } = this.state;

        return (
            <div>
                <div className="card card-small mb-4">
                    <div className="card-body py-3">
                        <div className="d-flex flex-wrap align-items-end">
                            <div className="mr-2 mb-2">
                                <label className="d-block" style={{ fontSize: 12 }}>Día</label>
                                {/* Borrar el campo emite `onChange(null)`. Ignorarlo es lo correcto:
                                    antes disparaba un pedido sin fecha, el backend respondía HOY y
                                    la pantalla mostraba el día en curso con el campo vacío y la URL
                                    en `?date=` — un link que ya no apuntaba a lo que se veía. */}
                                <DatePicker
                                    selected={this.state.dia}
                                    onChange={(d) => { if (d instanceof Date && !Number.isNaN(d.getTime())) this.setState({ dia: d }, this.cargar); }}
                                    dateFormat="dd/MM/yyyy"
                                    className="form-control"
                                />
                            </div>
                            {/* Moverse de a un día es la acción más frecuente de esta pantalla:
                                se mira un día y se compara con el de al lado. */}
                            <div className="btn-group btn-group-sm mb-2 mr-3">
                                <button className="btn btn-white" onClick={() => this.mover(-1)}>◀ Día anterior</button>
                                <button className="btn btn-white" onClick={() => this.mover(1)}>Día siguiente ▶</button>
                            </div>
                            {data && (
                                <div className="mb-2">
                                    <span className="text-capitalize"><strong>{data.weekday}</strong></span>
                                    {data.partial && (
                                        <span className="badge badge-warning ml-2">
                                            día en curso — medido hasta las {String(data.upTo).slice(0, 5)}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {!loaded && <div className="text-center text-muted py-5">Cargando…</div>}
                {loaded && error && <div className="alert alert-warning">{error}</div>}

                {/* Un día sin ventas es un dato, no un error: hay que decirlo en una línea en vez
                    de dejar una pantalla llena de guiones que se lee como que algo falló. */}
                {loaded && data && data.summary.orders === 0 && (
                    <div className="alert alert-secondary">
                        {data.partial
                            ? <>Todavía no hubo ventas hoy — medido hasta las {String(data.upTo).slice(0, 5)}.</>
                            : <>El <strong>{data.weekday} {data.date}</strong> no se registró ninguna venta.</>}
                        {data.cash.opened
                            ? ' La caja sí se abrió.'
                            : data.partial
                                ? ' La caja todavía no se abrió.'
                                : ' La caja tampoco se abrió: probablemente el local estuvo cerrado.'}
                    </div>
                )}

                {loaded && data && data.summary.orders > 0 && (
                    <div>
                        {/* ── La cifra del día ── */}
                        <div className="row">
                            {/* "Vendido" = lo que pagaron los clientes; va con las ventas y el ticket.
                                El ingreso le resta el crédito de los usados vendidos (plata de los
                                vendedores): ver el mismo criterio en SalesAnalyticsComponent. */}
                            {this.renderKpi('Vendido', money(data.summary.revenue),
                                `${number(data.summary.orders)} ventas · ${number(data.summary.units)} unidades`)}
                            {this.renderKpi('Ticket promedio', money(data.summary.avgTicket))}
                            {this.renderKpi('Margen bruto', money(data.summary.margin), 'ingreso de línea menos costo')}
                            {this.renderKpi('Queda después de todo', money(data.summary.waterfall.contributionMargin),
                                ayudaDelMargen(data.deductionCoverage))}
                        </div>

                        {/* El paso de lo vendido al ingreso, sólo si hubo usados o gift cards (un
                            backend anterior no manda `usedGames` ni `giftCards`). */}
                        {pasoAlIngreso(data.summary) && (
                            <p className="text-muted mb-3" style={{ fontSize: 12 }}>
                                Ingreso del día: <strong>{money(data.summary.netRevenue)}</strong> — lo vendido{' '}
                                {pasoAlIngreso(data.summary)}.
                            </p>
                        )}

                        {/* ── Contra qué se compara ── */}
                        <div className="card card-small mb-4">
                            <div className="card-header border-bottom">
                                <h6 className="m-0">Cómo estuvo contra lo habitual</h6>
                                <span className="text-muted" style={{ fontSize: 12 }}>
                                    Un número suelto no dice nada: {money(data.summary.revenue)} puede ser un {data.weekday} flojo
                                    o uno histórico. Se compara contra el mismo día de la semana, no contra el día anterior
                                    —un domingo contra un sábado es ruido.
                                </span>
                            </div>
                            <div className="card-body p-0">
                                <table className="table table-sm mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="border-0">Referencia</th>
                                            <th className="border-0 text-right">Vendido</th>
                                            <th className="border-0 text-right">Este día</th>
                                            <th className="border-0 text-right">Variación</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>El {data.weekday} anterior <span className="text-muted">({data.references.sameWeekdayLastWeek.date})</span></td>
                                            <td className="text-right">{money(data.references.sameWeekdayLastWeek.revenue)}</td>
                                            <td className="text-right">{money(data.summary.revenue)}</td>
                                            <td className="text-right"><Variacion pct={data.references.sameWeekdayLastWeek.changePct} /></td>
                                        </tr>
                                        <tr>
                                            <td>
                                                Promedio de los últimos {data.weekday}s
                                                <span className="text-muted"> ({number(data.references.sameWeekdayAverage.samples)} con ventas)</span>
                                            </td>
                                            <td className="text-right">{money(data.references.sameWeekdayAverage.revenue)}</td>
                                            <td className="text-right">{money(data.summary.revenue)}</td>
                                            <td className="text-right"><Variacion pct={data.references.sameWeekdayAverage.changePct} /></td>
                                        </tr>
                                        <tr>
                                            <td>El año pasado <span className="text-muted">({data.references.sameDayLastYear.date})</span></td>
                                            <td className="text-right">{money(data.references.sameDayLastYear.revenue)}</td>
                                            <td className="text-right">{money(data.summary.revenue)}</td>
                                            <td className="text-right"><Variacion pct={data.references.sameDayLastYear.changePct} /></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {this.renderHoras(data.hourly)}

                        <div className="row">
                            {this.renderBarras('Por canal', data.byChannel, 'Unidad de negocio.')}
                            {this.renderBarras('Por origen', data.byOriginator, 'Por dónde entró la venta.')}
                            {this.renderBarras('Por medio de pago', data.byPaymentType, 'Efectivo y transferencia no pagan comisión.')}
                        </div>

                        <div className="row">
                            {/* ── Lo más vendido ── */}
                            <div className="col-lg-6 col-md-12 mb-4">
                                <div className="card card-small h-100">
                                    <div className="card-header border-bottom">
                                        <h6 className="m-0">Lo que más se vendió</h6>
                                        {/* El corte por canal no es cosmético: un café se vende
                                            veinte veces por día y un juego una, así que el ranking
                                            mezclado lo copan siempre cafetería y mesas y los juegos
                                            no aparecen nunca. */}
                                        <span className="text-muted" style={{ fontSize: 12 }}>
                                            Ordenado por plata. Mirado en conjunto lo copan cafetería y mesas
                                            —se venden muchas veces por día— así que conviene verlo por canal.
                                        </span>
                                        <div className="btn-group btn-group-sm mt-2 d-block">
                                            <button
                                                className={`btn ${this.state.canalTop === null ? 'btn-primary' : 'btn-white'}`}
                                                onClick={() => this.setState({ canalTop: null })}
                                            >Todo junto</button>
                                            {data.topProducts.byChannel.map((c) => (
                                                <button
                                                    key={c.channel}
                                                    className={`btn ${this.state.canalTop === c.channel ? 'btn-primary' : 'btn-white'}`}
                                                    onClick={() => this.setState({ canalTop: c.channel })}
                                                >{c.channel}</button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="card-body p-0">
                                        {/* En `mesas` la única línea sería "Reserva de mesa" y su
                                            número, que no dice nada. La pregunta ahí es OTRA: qué
                                            mesa se usó y cuánta gente pasó por ella. */}
                                        {this.state.canalTop === 'mesas' ? this.renderMesas(data.byTable) : (() => {
                                            const sel = this.state.canalTop === null
                                                ? { items: data.topProducts.all, total: null }
                                                : (data.topProducts.byChannel.find((c) => c.channel === this.state.canalTop) || { items: [], total: 0 });
                                            return (
                                                <table className="table table-sm mb-0">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th className="border-0">Producto</th>
                                                            <th className="border-0 text-right">Unidades</th>
                                                            <th className="border-0 text-right">Vendido</th>
                                                            <th className="border-0 text-right">Margen</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {sel.items.length === 0 && <tr><td colSpan="4" className="text-center text-muted py-3">Sin ventas de ese canal ese día.</td></tr>}
                                                        {sel.items.map((t) => (
                                                            <tr key={t.code}>
                                                                <td>{t.name}</td>
                                                                <td className="text-right">{number(t.units)}</td>
                                                                <td className="text-right">{money(t.revenue)}</td>
                                                                <td className="text-right">{money(t.margin)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    {/* Cuántos productos distintos vendió ese canal: sin esto, un top
                                                        de 8 sobre 22 se lee como "esto es todo lo que se vendió". */}
                                                    {sel.total != null && sel.total > sel.items.length && (
                                                        <tfoot><tr><td colSpan="4" className="text-muted text-center py-2" style={{ fontSize: 12 }}>
                                                            Se muestran {number(sel.items.length)} de {number(sel.total)} productos distintos de este canal.
                                                        </td></tr></tfoot>
                                                    )}
                                                </table>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>

                            {/* ── Quiénes vinieron ── */}
                            <div className="col-lg-6 col-md-12 mb-4">
                                <div className="card card-small h-100">
                                    <div className="card-header border-bottom">
                                        <h6 className="m-0">Quiénes vinieron</h6>
                                    </div>
                                    <div className="card-body p-3">
                                        <p className="mb-2">
                                            <strong>{number(data.customers.people)}</strong> clientes identificados
                                            {' '}en {number(data.customers.identifiedSales)} de {number(data.customers.sales)} ventas.
                                            {' '}<span className="text-muted" style={{ fontSize: 12 }}>
                                                El resto salió como consumidor final, que no es un cliente: es la ausencia de uno.
                                            </span>
                                        </p>
                                        <p className="mb-0">
                                            <strong>{number(data.customers.firstTime)}</strong> compraban por primera vez
                                            {' '}y <strong>{number(data.customers.returning)}</strong> ya habían venido antes.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Salón y cafetería ── */}
                        <div className="card card-small mb-4">
                            <div className="card-header border-bottom">
                                <h6 className="m-0">Salón y cafetería</h6>
                                {/* Los dos porcentajes son distintos A PROPÓSITO. Una pareja en una
                                    mesa de 6 la ocupa entera y no se le puede decir que no: por eso
                                    llenar sillas es mucho más difícil que llenar mesas, y una
                                    ocupación de sillas baja NO significa que sobre lugar. */}
                                <span className="text-muted" style={{ fontSize: 12 }}>
                                    La ocupación de <strong>mesas</strong> dice si quedaba lugar para sentar a alguien;
                                    la de <strong>sillas</strong>, qué tan llenas estaban. Son distintas a propósito:
                                    una pareja en una mesa de seis la ocupa entera.
                                </span>
                            </div>
                            <div className="card-body p-3">
                                {data.salon.reservations === 0 && <p className="text-muted mb-0">Ese día no se usó el salón.</p>}
                                {data.salon.reservations > 0 && (
                                    <div>
                                        <div className="row mb-3">
                                            <div className="col-md-3 col-6 mb-2">
                                                <span className="text-muted d-block" style={{ fontSize: 11 }}>PICO DE OCUPACIÓN</span>
                                                {/* Los porcentajes son nullables: sin mesas activas el
                                                    backend manda null y un `.toFixed()` directo
                                                    reventaría la pantalla entera con un TypeError. */}
                                                <strong>{data.salon.peak && data.salon.peak.tablesPct != null
                                                    ? `${data.salon.peak.tablesPct.toFixed(0)}% de mesas` : '—'}</strong>
                                                {data.salon.peak && (
                                                    <span className="d-block text-muted" style={{ fontSize: 11 }}>
                                                        a las {data.salon.peak.hour}:00 · {data.salon.peak.tables} de {number(data.salon.tablesTotal)} mesas
                                                        {data.salon.peak.seatsPct != null && <>{' · '}{data.salon.peak.seatsPct.toFixed(0)}% de sillas</>}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="col-md-3 col-6 mb-2">
                                                <span className="text-muted d-block" style={{ fontSize: 11 }}>PROMEDIO CON GENTE</span>
                                                <strong>{data.salon.avgTablesPct == null ? '—' : `${data.salon.avgTablesPct.toFixed(0)}% de mesas`}</strong>
                                                <span className="d-block text-muted" style={{ fontSize: 11 }}>
                                                    {data.salon.avgSeatsPct == null ? '—' : `${data.salon.avgSeatsPct.toFixed(0)}% de sillas`}
                                                    {' · '}sobre las horas con actividad, no sobre 24
                                                </span>
                                            </div>
                                            <div className="col-md-3 col-6 mb-2">
                                                <span className="text-muted d-block" style={{ fontSize: 11 }}>MESAS QUE SE USARON</span>
                                                <strong>{number(data.salon.tablesUsed)} de {number(data.salon.tablesTotal)}</strong>
                                                <span className="d-block text-muted" style={{ fontSize: 11 }}>
                                                    a lo largo del día, no a la vez
                                                </span>
                                            </div>
                                            <div className="col-md-3 col-6 mb-2">
                                                <span className="text-muted d-block" style={{ fontSize: 11 }}>GASTO POR PERSONA</span>
                                                <strong>{money(data.salon.spendPerPerson)}</strong>
                                                <span className="d-block text-muted" style={{ fontSize: 11 }}>
                                                    entrada + consumo, sobre {number(data.salon.people)} personas
                                                </span>
                                            </div>
                                        </div>

                                        {/* La curva de ocupación del día, en mesas. */}
                                        <div className="d-flex align-items-end mb-2" style={{ height: 90, gap: 4 }}>
                                            {data.salon.hourly.map((h) => (
                                                <div key={h.hour} className="text-center" style={{ flex: 1, minWidth: 18 }}
                                                    title={`${h.hour}:00 · ${h.tables} mesas${h.tablesPct == null ? '' : ` (${h.tablesPct.toFixed(0)}%)`} · ${h.seats} personas${h.seatsPct == null ? '' : ` (${h.seatsPct.toFixed(0)}%)`}`}>
                                                    <div style={{
                                                        height: `${Math.max(3, (h.tablesPct || 0) * 0.7)}px`,
                                                        background: data.salon.peak && h.hour === data.salon.peak.hour ? '#007bff' : '#a8c7f0',
                                                        borderRadius: '3px 3px 0 0',
                                                    }} />
                                                    <span className="text-muted" style={{ fontSize: 10 }}>{h.hour}</span>
                                                </div>
                                            ))}
                                        </div>

                                        <p className="mb-1 text-muted" style={{ fontSize: 13 }}>
                                            <strong>{number(data.salon.reservations)}</strong> estadías
                                            {' '}({number(data.salon.walkIns)} sin reserva previa) ·
                                            {' '}<strong>{number(data.salon.people)}</strong> personas ·
                                            {' '}{data.salon.avgPartySize == null ? '—' : data.salon.avgPartySize.toFixed(1)} por mesa ·
                                            {' '}rotación {data.salon.turnsPerTable == null ? '—' : `${data.salon.turnsPerTable.toFixed(2)} estadías por mesa`} ·
                                            {' '}estadía media {data.salon.avgStayMinutes == null
                                                ? '—'
                                                : `${Math.round(data.salon.avgStayMinutes)} min`}
                                            {data.salon.staysWithoutDuration > 0 && (
                                                <span> <span className="text-warning">({number(data.salon.staysWithoutDuration)} sin duración medible)</span></span>
                                            )}
                                        </p>
                                        <p className="mb-0 text-muted" style={{ fontSize: 13 }}>
                                            Mesas {money(data.salon.tableRevenue)} + cafetería {money(data.salon.cafeteriaRevenue)}
                                            {data.salon.cafeteriaUnlinked > 0 && (
                                                <span> — de los cuales {money(data.salon.cafeteriaUnlinked)} no quedaron asociados a una mesa,
                                                    así que cuentan en el gasto por persona igual: no están sueltos porque el cliente
                                                    no estuviera sentado, sino porque no se asoció.</span>
                                            )}
                                            {' · '}{number(data.salon.kitchenOrders)} comandas de cocina
                                            {/* Mediana y no promedio, y sólo sobre las entregas marcadas
                                                de a una: el paso "listo" se marca EN LOTE, así que el
                                                promedio global mide el click y no la cocina. */}
                                            {data.salon.prepMedianMinutes != null
                                                ? `, ${Math.round(data.salon.prepMedianMinutes)} min de preparación (mediana de ${number(data.salon.prepMeasuredOn)} entregas marcadas de a una).`
                                                : data.salon.prepDelivered > 0
                                                    ? '. El tiempo de preparación no es medible ese día: las entregas se marcaron en lote.'
                                                    : '. Ninguna llegó a marcarse como entregada, así que no hay tiempo de preparación.'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ── Caja ── */}
                        <div className="card card-small mb-4">
                            <div className="card-header border-bottom">
                                <h6 className="m-0">Caja</h6>
                                <span className="text-muted" style={{ fontSize: 12 }}>
                                    Efectivo del día: {money(data.cash.cashSalesToday)}.
                                    {data.cash.cashAttributable === false && ' Con más de una caja el efectivo no se puede repartir entre ellas, así que el descuadre se informa por día.'}
                                </span>
                            </div>
                            <div className="card-body p-3">
                                {!data.cash.opened && <p className="text-muted mb-0">Ese día no se abrió la caja.</p>}
                                {data.cash.opened && data.cash.registers.map((r) => (
                                    <p key={r.user} className="mb-1" style={{ fontSize: 13 }}>
                                        <strong>{r.user}</strong> — apertura {money(r.openMoney)} · retiros {money(r.retirement)} · contado {money(r.counted)}
                                        {r.difference != null && (
                                            <span className={Math.abs(r.difference) < 1 ? 'text-success' : 'text-danger'}>
                                                {' '}· diferencia {money(r.difference)}
                                            </span>
                                        )}
                                    </p>
                                ))}
                                {data.cash.opened && data.cash.cashAttributable === false && data.cash.day && (
                                    <p className="mb-0 mt-2 pt-2 border-top" style={{ fontSize: 13 }}>
                                        <strong>Del día</strong> — debería cerrar con {money(data.cash.day.expected)} ·
                                        {' '}contado {money(data.cash.day.counted)}
                                        {data.cash.day.difference != null && (
                                            <span className={Math.abs(data.cash.day.difference) < 1 ? 'text-success' : 'text-danger'}>
                                                {' '}· diferencia {money(data.cash.day.difference)}
                                            </span>
                                        )}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* ── Lo que no salió bien ── */}
                        <div className="card card-small mb-4">
                            <div className="card-header border-bottom">
                                <h6 className="m-0">Lo que no salió bien</h6>
                                <span className="text-muted" style={{ fontSize: 12 }}>
                                    Si está todo en cero, el día salió limpio.
                                </span>
                            </div>
                            <div className="card-body p-3">
                                <div className="row">
                                    <div className="col-md-3 col-6 mb-2">
                                        <span className="text-muted d-block" style={{ fontSize: 11 }}>VENTAS ANULADAS</span>
                                        <strong>{number(data.issues.cancelledSales)}</strong>
                                        {data.issues.cancelledSales > 0 && <span className="text-muted"> ({money(data.issues.cancelledAmount)})</span>}
                                    </div>
                                    <div className="col-md-3 col-6 mb-2">
                                        <span className="text-muted d-block" style={{ fontSize: 11 }}>VENTAS CON CUPÓN</span>
                                        <strong>{number(data.issues.salesWithCoupon)}</strong>
                                    </div>
                                    <div className="col-md-3 col-6 mb-2">
                                        <span className="text-muted d-block" style={{ fontSize: 11 }}>ERRORES DEL SISTEMA</span>
                                        <strong className={data.issues.systemErrors > 0 ? 'text-danger' : ''}>{number(data.issues.systemErrors)}</strong>
                                    </div>
                                    <div className="col-md-3 col-6 mb-2">
                                        <span className="text-muted d-block" style={{ fontSize: 11 }}>ÓRDENES DE ML FALLIDAS</span>
                                        <strong className={data.issues.failedMlOrders > 0 ? 'text-danger' : ''}>{number(data.issues.failedMlOrders)}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default DayAnalyticsComponent;
